import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import FloatingButtons from '../home/components/FloatingButtons';
import { useInView } from '../home/hooks/useInView';

const SaintGaudens = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    document.title = 'Réparation de fenêtres à Saint-Gaudens – Intervention rapide 24h/24 | Vitrerie 31';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert réparation fenêtres à Saint-Gaudens (31800). Intervention sous 3h pour fenêtres PVC, bois, alu. Devis gratuit. ☎ 05 54 54 25 45');
    }
  }, []);

  const services = [
    { icon: 'ri-tools-line', title: 'Réparation Fenêtres PVC', desc: 'Joints, mécanismes, vitrage' },
    { icon: 'ri-hammer-line', title: 'Fenêtres Bois', desc: 'Rénovation et entretien' },
    { icon: 'ri-window-line', title: 'Fenêtres Aluminium', desc: 'Réparation et remplacement' },
    { icon: 'ri-shield-check-line', title: 'Vitrerie d\'urgence', desc: 'Intervention 24h/24' }
  ];

  const testimonials = [
    {
      name: 'Pierre Dubois',
      location: 'Centre-ville Saint-Gaudens',
      text: 'Fenêtre ancienne en bois à rénover. Travail remarquable, respect du patrimoine. Équipe très professionnelle.',
      rating: 5
    },
    {
      name: 'Marie Lacoste',
      location: 'Quartier Valentine',
      text: 'Remplacement de double vitrage cassé. Intervention rapide malgré la distance depuis Toulouse. Très satisfaite !',
      rating: 5
    },
    {
      name: 'Jacques Bertrand',
      location: 'Quartier Roquettes',
      text: 'Problème d\'isolation thermique résolu. Prix honnête et travail soigné. Je recommande vivement !',
      rating: 5
    }
  ];

  const problemesTypiques = [
    { icon: 'ri-snowy-line', title: 'Climat montagnard', desc: 'Isolation renforcée nécessaire' },
    { icon: 'ri-water-flash-line', title: 'Condensation', desc: 'Humidité et double vitrage' },
    { icon: 'ri-ancient-gate-line', title: 'Fenêtres anciennes', desc: 'Patrimoine et rénovation' },
    { icon: 'ri-wind-line', title: 'Courants d\'air', desc: 'Joints usés et isolation' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-orange-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-map-pin-line"></i>
              <span>Saint-Gaudens - Haute-Garonne (31800)</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Réparation de fenêtres à <strong className="text-teal-600">Saint-Gaudens</strong>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Intervention rapide <strong>sous 3h</strong> pour tous vos problèmes de fenêtres
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a
                href="tel:0554542545"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-700 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
              >
                <i className="ri-phone-line text-xl"></i>
                05 54 54 25 45
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer border-2 border-teal-600 active:scale-95"
              >
                <i className="ri-message-line text-xl"></i>
                Devis gratuit
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Intervention rapide</h3>
                <p className="text-gray-600 text-sm">Sous 3h à Saint-Gaudens</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-2-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">À 90 km de Toulouse</h3>
                <p className="text-gray-600 text-sm">Tous quartiers desservis</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-24-hours-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Urgences 24h/24</h3>
                <p className="text-gray-600 text-sm">7 jours sur 7</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos services à <strong className="text-teal-600">Saint-Gaudens</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experts en réparation de fenêtres pour tous les quartiers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${service.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problèmes Typiques */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Problèmes fréquents à <strong className="text-teal-600">Saint-Gaudens</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Nous connaissons les spécificités de votre commune
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemesTypiques.map((probleme, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${probleme.icon} text-2xl text-orange-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{probleme.title}</h3>
                <p className="text-gray-600 text-sm">{probleme.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-teal-500 to-orange-500 rounded-lg p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Pourquoi ces problèmes à Saint-Gaudens ?</h3>
            <p className="text-lg mb-6 opacity-90 max-w-3xl mx-auto">
              Située à <strong>90 km au sud-ouest de Toulouse</strong>, Saint-Gaudens est une ville historique au pied des Pyrénées. Le <strong>climat montagnard</strong> et les <strong>variations de température</strong> importantes causent des problèmes d'isolation et de condensation. Les quartiers <strong>Centre-ville, Valentine et Roquettes</strong> comptent de nombreuses fenêtres anciennes nécessitant une expertise particulière en rénovation.
            </p>
          </div>
        </div>
      </section>

      {/* Témoignages Clients */}
      <section ref={testimonialsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Avis de nos clients à <strong className="text-teal-600">Saint-Gaudens</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ils nous ont fait confiance pour leurs fenêtres
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-500 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quartiers desservis */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tous les quartiers de <strong className="text-teal-600">Saint-Gaudens</strong>
            </h2>
            <p className="text-lg text-gray-600">Intervention rapide dans toute la commune</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {['Centre-ville', 'Valentine', 'Roquettes', 'Planté', 'Barris', 'Pradeau', 'Montmajou', 'Encausse'].map((quartier, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all text-center"
              >
                <i className="ri-map-pin-2-fill text-2xl text-teal-600 mb-2"></i>
                <p className="font-semibold text-gray-900">{quartier}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Besoin d'une réparation de fenêtre à Saint-Gaudens ?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Intervention rapide sous 3h - Devis gratuit - Urgences 24h/24
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:0554542545"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-phone-line text-xl"></i>
              Appelez maintenant
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-message-line text-xl"></i>
              Demander un devis
            </a>
          </div>
        </div>
      </section>

      {/* Liens vers autres villes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Nous intervenons aussi dans les villes voisines
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Toulouse</a>
            <span className="text-gray-400">•</span>
            <a href="/colomiers" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Colomiers</a>
            <span className="text-gray-400">•</span>
            <a href="/blagnac" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Blagnac</a>
            <span className="text-gray-400">•</span>
            <a href="/tournefeuille" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Tournefeuille</a>
            <span className="text-gray-400">•</span>
            <a href="/muret" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Muret</a>
            <span className="text-gray-400">•</span>
            <a href="/balma" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Balma</a>
            <span className="text-gray-400">•</span>
            <a href="/ramonville" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Ramonville</a>
            <span className="text-gray-400">•</span>
            <a href="/cugnaux" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Cugnaux</a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default SaintGaudens;